import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { queryKeys, invalidateQueries } from '../lib/queryKeys';
import { getApiErrorMessage } from '../lib/apiErrors';
import { showToast } from '../lib/toast';
import type { NotificationFilterRequest, MarkNotificationsReadRequest } from '../types/notifications';

/**
 * Get paginated notifications list with filters
 */
export function useNotifications(filter: NotificationFilterRequest = {}, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.notifications.list(filter),
    queryFn: async () => {
      const response = await apiService.getNotifications(filter);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch notifications'));
      }
      return response.data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

/**
 * Get unread notification count for badge display
 */
export function useUnreadNotificationCount(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.notifications.count(),
    queryFn: async () => {
      const response = await apiService.getNotificationCount();
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch notification count'));
      }
      return response.data;
    },
    enabled,
    // Poll every 30 seconds for real-time-ish updates
    // This supplements SignalR for reliability
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  });
}

/**
 * Get a single notification by ID
 */
export function useNotification(id: string) {
  return useQuery({
    queryKey: queryKeys.notifications.detail(id),
    queryFn: async () => {
      const response = await apiService.getNotification(id);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch notification'));
      }
      return response.data;
    },
    enabled: !!id,
  });
}

/**
 * Mark a single notification as read
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.markNotificationAsRead(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to mark notification as read'));
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate both the list and count queries
      queryClient.invalidateQueries({ queryKey: invalidateQueries.notifications.lists() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.notifications.count() });
    },
  });
}

/**
 * Mark all notifications as read
 */
export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiService.markAllNotificationsAsRead();
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to mark all notifications as read'));
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invalidateQueries.notifications.all() });
    },
    onError: (error: Error) => {
      showToast.error('Mark all read failed', error.message || 'Failed to mark all notifications as read.');
    },
  });
}

/**
 * Mark multiple notifications as read
 */
export function useMarkMultipleNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: MarkNotificationsReadRequest) => {
      const response = await apiService.markMultipleNotificationsAsRead(request);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to mark notifications as read'));
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invalidateQueries.notifications.lists() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.notifications.count() });
    },
  });
}

/**
 * Delete a single notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.deleteNotification(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to delete notification'));
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invalidateQueries.notifications.all() });
    },
  });
}

/**
 * Delete all read notifications
 */
export function useDeleteAllReadNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiService.deleteAllReadNotifications();
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to delete read notifications'));
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invalidateQueries.notifications.all() });
    },
    onError: (error: Error) => {
      showToast.error('Clear read failed', error.message || 'Failed to delete read notifications.');
    },
  });
}

/**
 * Sync notification indicators with unread count:
 * - Document title: Works in all browsers (shows "(3) EU ServiceLine...")
 * - PWA app badge: Works on iOS 16.4+ and Chrome/Edge desktop PWAs
 * On Android, badges are handled automatically by the OS from notifications.
 */
export function useAppBadgeSync() {
  const { data } = useUnreadNotificationCount();
  const unreadCount = data?.unreadCount ?? 0;

  // Update document title with unread count (works in all browsers)
  useEffect(() => {
    const baseTitle = 'EU ServiceLine - Customer Support Platform';
    document.title = unreadCount > 0 ? `(${unreadCount}) ${baseTitle}` : baseTitle;
  }, [unreadCount]);

  // Update PWA app badge when unread count changes
  useEffect(() => {
    // Feature detection for Badging API
    if (!('setAppBadge' in navigator)) return;

    const nav = navigator as Navigator & {
      setAppBadge: (count?: number) => Promise<void>;
      clearAppBadge: () => Promise<void>;
    };

    if (unreadCount > 0) {
      nav.setAppBadge(unreadCount).catch(() => {
        // Ignore errors - badge is best-effort
      });
    } else {
      nav.clearAppBadge().catch(() => {
        // Ignore errors - badge is best-effort
      });
    }
  }, [unreadCount]);
}
